<script lang="ts" setup>
import DefinitionListSection from '../../DefinitionListSection.vue';
import HeadlineListSection from '../../HeadlineListSection.vue';
import ParagrahNote from '../../ParagrahNote.vue';
import ParagrahNotice from '../../ParagrahNotice.vue';
import ScrollTable from '../../ScrollTable.vue';
import WhereToConsultCard from '../../WhereToConsult/WhereToConsultCard.vue';
import InterestRatesImg from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/mortgage/WhereToConsultCard/03_interest_rates.png/03_interest_rates.webp';
import DifferenceInRepaymentImg from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/mortgage/WhereToConsultCard/04_difference_in_repayment.png/04_difference_in_repayment.webp';
import CreditorGroupInsurancesImg from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/mortgage/WhereToConsultCard/05_creditor_group_insurance.png/05_creditor_group_insurance.webp';
import LiveEventImg from '/home/runner/_work/shadow/shadow/packages/app/node_modules/.cache/home/runner/_work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/mortgage/WhereToConsultCard/06_liveevent.png/06_liveevent.webp';
import PinchImageModal from '~theme/components/organisms/PinchImageModal.vue';

defineProps<{
  imageNumber: number;
}>();
</script>

<template>
  <WhereToConsultCard
    title="住宅ローンを契約する前に確認したい8つのポイント"
    :image-number="imageNumber"
  >
    <p>
      住宅ローンの仕組みやコスト等をしっかり理解することで、自分にあった住宅ローンを見つけやすくなります。住宅ローン選びを始める前に、まずは以下の8つについて理解を深めましょう。
    </p>
    <HeadlineListSection>
      <li><a href="#card2Headline1">金利の種類</a></li>
      <li><a href="#card2Headline2">返済方法の種類</a></li>
      <li><a href="#card2Headline3">団信の保障の種類</a></li>
      <li><a href="#card2Headline4">フラット35について</a></li>
      <li><a href="#card2Headline5">住宅ローン控除について</a></li>
      <li><a href="#card2Headline6">繰り上げ・借り換えについて</a></li>
      <li><a href="#card2Headline7">借入時にかかる各種手数料</a></li>
      <li><a href="#card2Headline8">将来発生しうるイベント</a></li>
    </HeadlineListSection>

    <h3 id="card2Headline1">金利の種類</h3>
    <p>
      住宅ローン金利の種類は、大きく分けて「<strong>全期間固定金利型</strong>」「<strong>変動金利型</strong>」「<strong>固定金利期間選択型</strong>」の3種類があります。
    </p>
    <PinchImageModal :src="InterestRatesImg" />
    <p>
      「全期間固定金利型」は、借入から完済まで金利が変動せず、変動金利型や固定金利期間選択型よりも金利が高い傾向にあります。金利変動リスクがなく、返済額が一定で返済計画を立てやすい点がメリットです。
    </p>
    <p>
      「変動金利型」は金利変動リスクがありますが、固定金利型よりも金利が低い傾向にあるため、金利を抑えたい方におすすめです。
    </p>
    <p>
      「固定金利期間選択型」は、一定期間だけ固定金利が適用され、固定期間が終了するタイミングで以降の金利タイプを選び直せたり、銀行によっては自動的に変動金利型へ移行されます。全期間固定金利型より金利が低い傾向なので、金利をある程度抑えつつ、一定期間の金利変動リスクを抑えたい方におすすめです。
    </p>
    <p>
      2024年3月に日銀がマイナス金利解除を決めました。急激に金利が引き上がることはないと予想されますが、引き続き金利動向には注目しましょう。
    </p>

    <h3 id="card2Headline2">返済方法の種類</h3>
    <p>
      住宅ローンの返済方法は「<strong>元利均等返済</strong> 」と「<strong
        >元金均等返済</strong
      >
      」の2種類があります。
    </p>
    <PinchImageModal :src="DifferenceInRepaymentImg" />
    <p>
      「元利均等返済」は、元本と利息を合わせた返済額が毎月均等になるように返済する方法です。ローン残高に応じて利息を計算するため、利息の比率は返済当初が最も高く、返済が進むにつれてだんだん低くなっていきます。
    </p>
    <p>
      借入時から完済まで毎月返済額が一定のため、返済計画を立てやすく一般的に多く利用されています。
    </p>
    <p>
      「元金均等返済」は、元金の返済額を毎月均等にして、そこにローン残高に応じた利息を乗せて返済する方法です。毎月の返済額は返済当初が最も多く、返済が進むにつれてだんだん少なくなっていきます。
    </p>
    <p>
      利息を抑えて総返済額を少なくしたい方や、返済当初よりも将来の毎月の返済額を少なくしたい方におすすめです。
    </p>

    <h3 id="card2Headline3">団信の保障の種類</h3>
    <p>
      ほとんどの住宅ローンでは、団体信用生命保険（団信）への加入が契約の必須条件です。団信に加入すると、<strong>契約者が死亡・高度障害状態になった場合に保険金で住宅ローンが完済される</strong>ため、万一の際に返済が滞り家を失う事態を避けられます。
    </p>
    <PinchImageModal :src="CreditorGroupInsurancesImg" />
    <p>
      民間の銀行の住宅ローンの場合、死亡・高度障害を保障する一般的な団信の保険料は金利に含まれており、基本的に無料です。なかには金利上乗せなしでプラスアルファの保障が付帯している住宅ローンもあります。
    </p>
    <p>
      団信にはガン保障や介護保障、3大疾病保障などのオプションを付けられますが、保障の付加に金利が上乗せされるのが一般的です。団信は中途解約ができないので、本当に必要な保障なのか慎重に考えましょう。
    </p>
    <ScrollTable>
      <thead>
        <tr>
          <th colspan="4">団体信用生命保険の保障内容の一例</th>
        </tr>
        <tr>
          <th>事由</th>
          <th>詳細</th>
          <th>保障内容</th>
          <th>一般的な上乗せ金利※</th>
        </tr>
      </thead>
      <tr>
        <th>死亡・高度障害</th>
        <td>死亡または高度障害に該当したとき</td>
        <td rowspan="3">住宅ローン残高が0円</td>
        <td>なし</td>
      </tr>
      <tr>
        <th>がん</th>
        <td>所定の悪性新生物と診断確定されたとき</td>
        <td>+0.1%</td>
      </tr>
      <tr>
        <th>3大疾病</th>
        <td>
          <ul>
            <li>所定の悪性新生物と診断確定されたとき</li>
            <li>
              脳卒中、急性心筋梗塞で所定の手術を受けた時または所定の状態が60日以上継続したとき
            </li>
          </ul>
        </td>
        <td>+0.2%</td>
      </tr>
      <tr>
        <th rowspan="2">全ての病気・ケガ</th>
        <td>所定の就業不能状態が3ヶ月を超えて継続したとき</td>
        <td>毎月の住宅ローン返済額を保障</td>
        <td rowspan="2">+0.3%</td>
      </tr>
      <tr>
        <td style="background-color: unset">
          所定の就業不能状態が12ヶ月を超えて継続した時
        </td>
        <td>住宅ローン残高が0円</td>
      </tr>
    </ScrollTable>

    <h3 id="card2Headline4">フラット35について</h3>
    <p>
      フラット35は、独立行政法人の住宅金融支援機構の提携金融機関が取り扱う、全期間固定金利型の住宅ローンです。借入から完済まで金利が変動しないのが特徴で、最長で35年間の借入ができます。
    </p>
    <p>
      フラット35を提供する金融機関は全国に300以上あり（2024年4月10日現在）、金利や融資手数料は金融機関によって異なりますが、利用条件はどの金融機関でも同じです
      。
    </p>

    <ScrollTable>
      <thead>
        <tr>
          <th colspan="2">フラット35の利用条件</th>
        </tr>
      </thead>
      <tr>
        <th>申込要件</th>
        <td>
          <ul>
            <li>申込時の年齢が満70歳未満</li>
            <li>日本国籍の方、永住許可を受けている方または特別永住者の方</li>
            <li>
              総返済負担率が年収400万円未満の場合は30%以下、年収400万円以下の場合は35%以下
            </li>
          </ul>
        </td>
      </tr>
      <tr>
        <th>資金使途</th>
        <td>本人またはその親族が住むための新築・中古住宅の建設・購入資金</td>
      </tr>
      <tr>
        <th>借り入れ対象となる住宅</th>
        <td>
          <ul>
            <li>住宅金融支援機構が定めた技術基準に適合する住宅</li>
            <li>
              住宅の床面積が一戸建て、連続建ておよび重ね建ての場合は70m<sup>2</sup>以上、共同建て<ParagrahNotice>(マンションなど)</ParagrahNotice>の場合は30m<sup>2</sup>以上
            </li>
          </ul>
        </td>
      </tr>
      <tr>
        <th>借入額</th>
        <td>
          100万円以上8,000万円以下<ParagrahNotice>(1万円単位)</ParagrahNotice>
        </td>
      </tr>
      <tr>
        <th>借入期間</th>
        <td>15〜35年</td>
      </tr>
      <tr>
        <th>借入金利</th>
        <td>全期間固定金利</td>
      </tr>
      <tr>
        <th>返済方法</th>
        <td>
          元利均等毎月払いまたは元利均等毎月払い<ParagrahNotice
            >(ボーナス払い併用可)</ParagrahNotice
          >
        </td>
      </tr>
      <tr>
        <th>保証人・保証料</th>
        <td>不要</td>
      </tr>
      <tr>
        <th>団体信用生命保険</th>
        <td>任意加入</td>
      </tr>
      <tr>
        <th>保証料・繰上返済手数料</th>
        <td>不要</td>
      </tr>
    </ScrollTable>

    <ParagrahNote>
      <NuxtLink
        href="https://www.flat35.com/loan/flat35/conditions.html"
        external
        target="_blank"
        >住宅金融支援機構 【フラット35】ご利用条件</NuxtLink
      >をもとに作成
    </ParagrahNote>
    <p>
      フラット35の申込要件は比較的緩く、年齢や国籍、総返済負担率の条件を満たしていれば申込が可能です。また、保証人・保証料が不要で団体信用生命保険も任意加入のため、銀行の住宅ローン利用が難しい方でも借入できる可能性があります。
    </p>
    <p>
      ただし、全期間固定金利型のため金利が高い傾向にあり、融資率によって金利が変動するため頭金がないと割高な金利が適用されます。
    </p>
    <p>
      住宅ローンの審査が通過できるか心配な方や、団体信用生命保険加入に懸念がある方はフラット35がおすすめですが、金利の安さを重視して住宅ローンを選びたい場合は、フラット35よりも変動金利型の住宅ローンがおすすめです。
    </p>

    <h3 id="card2Headline5">住宅ローン控除について</h3>
    <p>
      住宅ローンで住宅を購入した場合、一定の要件を満たせば住宅ローン控除を利用することにより税金の控除を受けられます。
    </p>
    <p>
      住宅ローン控除額：<strong>ローン残高×0.7％×13年間（中古住宅は10年間）</strong>>（2024年8月現在）
    </p>
    <p>
      住宅ローン控除額は入居年や住宅の種類によって上限があります。例えば、子育て世帯・若者夫婦世帯が2024年入居で長期優良住宅を購入した場合の控除限度額は35万円です（ローン残高5,000万円×0.7％＝35万円）。
    </p>
    <p>
      また、住宅ローン控除を受けるには、控除を受ける最初の年のみ確定申告が必要となり、2年目以降は会社員の場合は年末調整により控除を受けることができます。
    </p>

    <h3 id="card2Headline6">繰り上げ・借り換えについて</h3>
    <p>
      住宅ローンの利息負担を軽減するには、「繰り上げ返済」や「借り換え」が有効です。
    </p>
    <p>
      「繰り上げ返済」とは、月々の返済とは別に、住宅ローンの元金を予定よりも早く繰り上げて返済することです。
    </p>
    <DefinitionListSection>
      <dt>住宅ローン繰り上げ返済の概要</dt>
      <dd>
        <ul>
          <li>
            支払う利息が軽減され、住宅ローンの総支給額を減らすことができる
          </li>
          <li>好きなタイミングで実施可能</li>
          <li>
            住宅ローン控除額が減ってしまう場合があるので、適切なタイミングで実施する必要がある
          </li>
          <li>
            金融機関によっては1回の最低返済額が定められていたり、繰り上げ返済時に手数料がかかる場合がある
          </li>
        </ul>
      </dd>
    </DefinitionListSection>
    <p>
      ある程度の資産が貯まったときやボーナスが入ったときなどに、繰り上げ返済の実行を検討する人が多いことが予想されます。
    </p>
    <p>
      繰り上げ返済のほか、より低金利な住宅ローンへ「借り換える」ことも利息負担の軽減が可能です。
    </p>
    <p>
      借り換えには手数料等がかかりますが、一般的に借り換え先の金利が1%以上低く、借入残高が1,000万円以上、残りの返済期間が10年以上あれば借り換えメリットがあると言われています。
    </p>
    <p>
      ただし、いずれの場合も住宅ローンの返済期間が10年未満になると住宅ローン控除が使えなくなるため、住宅ローン控除をフル活用したい場合は、控除期間中に住宅ローンの返済期間が10年未満にならないタイミングと金額で、繰り上げ返済や借り換えを検討しましょう。
    </p>

    <h3 id="card2Headline7">借入時にかかる各種手数料</h3>

    <DefinitionListSection>
      <dt>住宅ローン借入時かかる手数料の一例（金融機関によって異なる）</dt>
      <dd>
        <ul>
          <li>事務手数料（定額型：数万円～30万円、定率型：借入額の2.2％）</li>
          <li>保証料（借入額の0％～2％）</li>
          <li>印紙代（0円～2万円）</li>
          <li>
            登記関連費用（登録免許税：借入額の0.4％、司法書士報酬：5万円～10万円）
          </li>
        </ul>
      </dd>
    </DefinitionListSection>
    <p>
      住宅ローンの借入時には、事務手数料、保証料、印紙代、登記関連費用などのさまざまな手数料がかかります。そのため、住宅ローンの借入時にかかる手数料負担も考慮しながら、無理のない借入額を検討することが大切です。
    </p>
    <p>
      特に住宅ローンの事務手数料は金額が大きく、定額型の場合は数万円から30万円程度、定率型の場合は借入額の2.2％（3,000万円の借入なら66万円）が相場となっています。
    </p>
    <p>
      また、銀行によっては住宅ローンの返済開始後も、繰り上げ返済時や金利タイプの変更時などに手数料がかかります。住宅ローンを選ぶ際は、金利だけでなく各種手数料の金額も必ず確認しましょう。
    </p>

    <h3 id="card2Headline8">将来発生しうるライフイベント</h3>
    <PinchImageModal :src="LiveEventImg" />
    <p>
      長い人生の中で、マイホーム購入以外にもさまざまなライフイベントが待っています。
    </p>
    <p>
      特に大きなお金が動きやすい「子どもの進学」「介護」「老後資金」などにもしっかり備えるため、どのタイミングでどのくらいお金が動くのかを確認したうえで、住宅購入額を検討しましょう。
    </p>
  </WhereToConsultCard>
</template>

<style module lang="scss"></style>
